// Constraints BETWEEN objects (DOM-free, unit-tested).
//
// Every constrained object belongs to a rigid BODY (a lone object, a whole
// group, a room with what stands in it). A network of constraints is solved in
// one sketch of the existing gcs solver:
//   stage 1 — every free body is RIGID (`rigid`: 3 DOF, a move and a turn);
//   stage 2 — if that conflicts, the free bodies that are one deformable shape
//             (polygon, wall, line, room…) may reshape: all their vertices enter
//             the sketch with their own locks and a strong preference for their
//             angles, and only the pieces referenced are tied to them;
//   else    — the network cannot be satisfied.
// Held bodies (the one just edited, the first pick of a new constraint, a fixed
// object) enter as fixed points and never move. The caller supplies the geometry
// through `piece` / `bodies`; this module never reads the drawing itself.

import type {
  ConstraintRef,
  ObjConstraintType,
  ObjectConstraint,
  Vec2,
} from '../shared/types';
import {
  type GcsConstraint,
  type GcsEdge,
  type GcsPoint,
  type GcsSketch,
  solveSketch,
} from './gcs';

/** What a reference resolves to: one point (a vertex) or two (an edge), in world
 *  mm, plus — for a deformable object — each point as a control vertex + offset
 *  (a wall face sits a fixed offset from its centre line). */
export interface PieceGeom {
  pts: Vec2[];
  ctrl?: Array<{ i: number; off: Vec2 }>;
}

export interface BodyInput {
  /** Two distinct world points of the body: its frame. */
  frame: [Vec2, Vec2];
  held: boolean;
  /** Present when the body is ONE shape allowed to deform (stage 2). */
  deform?: {
    obj: string;
    verts: Vec2[];
    closed: boolean;
    /** The shape's own constraints over ids `v<k>` (its locks / relations). */
    own?: GcsSketch;
    /** Vertices it would rather keep where they are (a circle's centre: it grows
     *  about it, not lopsidedly). */
    pin?: number[];
  };
}

export interface NetworkInput {
  constraints: ObjectConstraint[];
  bodyOf: (objId: string) => string;
  bodies: Map<string, BodyInput>;
  piece: (ref: ConstraintRef) => PieceGeom | null;
}

/** A rigid move: rotate by `rot` (radians, CCW) about `pivot`, then translate by
 *  (dx, dy). */
export interface BodyMove { pivot: Vec2; dx: number; dy: number; rot: number; }

export interface NetworkResult {
  ok: boolean;
  /** 1 = solved rigidly, 2 = some shapes deformed, 0 = not satisfiable. */
  stage: 0 | 1 | 2;
  moves: Map<string, BodyMove>;
  /** obj id → its new control vertices (stage 2 only). */
  deformed: Map<string, Vec2[]>;
}

type Pair =
  | 'cc'
  | 'ce'
  | 'ee'
  | 'o'
  | 've'
  | 'vv';

export const CONSTRAINT_META: Record<ObjConstraintType, { label: string; glyph: string; pair: Pair; valued?: 'mm' | 'deg' }> = {
  coincident: { label: 'Coincident', glyph: '◎', pair: 'vv' },
  horizontal: { label: 'Horizontal', glyph: 'H', pair: 'vv' },
  vertical: { label: 'Vertical', glyph: 'V', pair: 'vv' },
  distance: { label: 'Distance', glyph: '↔', pair: 'vv', valued: 'mm' },
  onLine: { label: 'On line', glyph: '∈', pair: 've' },
  midpoint: { label: 'Midpoint', glyph: 'M', pair: 've' },
  pointLineDistance: { label: 'Distance to line', glyph: '↕', pair: 've', valued: 'mm' },
  parallel: { label: 'Parallel', glyph: '∥', pair: 'ee' },
  perpendicular: { label: 'Perpendicular', glyph: '⊥', pair: 'ee' },
  collinear: { label: 'Collinear', glyph: '≡', pair: 'ee' },
  equal: { label: 'Equal', glyph: '=', pair: 'ee' },
  lineDistance: { label: 'Distance between lines', glyph: '⇔', pair: 'ee', valued: 'mm' },
  angle: { label: 'Angle', glyph: '∠', pair: 'ee', valued: 'deg' },
  // Two vertices mirrored about a line — a THIRD pick (`m`).
  symmetric: { label: 'Symmetric', glyph: '⋈', pair: 'vv' },
  concentric: { label: 'Concentric', glyph: '⊚', pair: 'cc' },
  equalRadius: { label: 'Equal radius', glyph: 'R=', pair: 'cc' },
  tangent: { label: 'Tangent', glyph: 'T', pair: 'cc' },
  fix: { label: 'Fixed', glyph: '▣', pair: 'o' },
};

/** Relations a curve pair offers beyond its own pair's: a line tangent to a curve. */
const ALSO: Partial<Record<Pair, ObjConstraintType[]>> = { ce: ['tangent'] };

/** Every piece a constraint holds: its two picks, and Symmetric's mirror line. */
export function refsOf(c: ObjectConstraint): ConstraintRef[] {
  return [c.a, ...(c.b ? [c.b] : []), ...(c.m ? [c.m] : [])];
}

const pairOf = (a: ConstraintRef['el'], b: ConstraintRef['el'] | undefined): Pair | null => {
  if (a === 'object' && !b) return 'o';
  if (a === 'vertex' && b === 'vertex') return 'vv';
  if (a === 'edge' && b === 'edge') return 'ee';
  if ((a === 'vertex' && b === 'edge') || (a === 'edge' && b === 'vertex')) return 've';
  if (a === 'curve' && b === 'curve') return 'cc';
  if ((a === 'curve' && b === 'edge') || (a === 'edge' && b === 'curve')) return 'ce';
  return null;
};

/** The constraint types that make sense between two pieces, in menu order. */
export function relationsFor(a: ConstraintRef['el'], b?: ConstraintRef['el']): ObjConstraintType[] {
  const p = pairOf(a, b);
  return [...(Object.keys(CONSTRAINT_META) as ObjConstraintType[]).filter((t) => CONSTRAINT_META[t].pair === p), ...(p ? ALSO[p] ?? [] : [])];
}

/** A vertex–edge pair is stored vertex first; the caller's pick order is kept
 *  otherwise (the first pick is the one that holds). */
export function orderRefs(a: ConstraintRef, b: ConstraintRef): [ConstraintRef, ConstraintRef] {
  return (a.el === 'edge' && b.el === 'vertex') || (a.el === 'edge' && b.el === 'curve') ? [b, a] : [a, b];
}

/** A point mirrored about the line a→b. */
export function reflectPoint(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const d = { x: b.x - a.x, y: b.y - a.y }, L2 = d.x * d.x + d.y * d.y || 1;
  const t = ((p.x - a.x) * d.x + (p.y - a.y) * d.y) / L2;
  const f = { x: a.x + d.x * t, y: a.y + d.y * t };
  return { x: 2 * f.x - p.x, y: 2 * f.y - p.y };
}

/** Whether two circles touch from inside (one in the other) rather than outside —
 *  which a Tangent keeps is decided by how they lie when it is made. */
function internalTangent(c1: Vec2, r1: number, c2: Vec2, r2: number): boolean {
  const d = Math.hypot(c2.x - c1.x, c2.y - c1.y);
  return Math.abs(d - Math.abs(r1 - r2)) < Math.abs(d - (r1 + r2));
}

const sub = (p: Vec2, q: Vec2): Vec2 => ({ x: p.x - q.x, y: p.y - q.y });
const len = (v: Vec2): number => Math.hypot(v.x, v.y);
const crossV = (u: Vec2, v: Vec2): number => u.x * v.y - u.y * v.x;
/** Signed distance of p from the line a→b (+ on the left). */
export function signedLineDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const d = sub(b, a); const L = len(d) || 1;
  return crossV(d, sub(p, a)) / L;
}
/** Directed angle from line a0→a1 to line b0→b1, in [0, 180). */
export function lineAngle(a0: Vec2, a1: Vec2, b0: Vec2, b1: Vec2): number {
  const u = sub(a1, a0), v = sub(b1, b0);
  let d = Math.atan2(crossV(u, v), u.x * v.x + u.y * v.y) * 180 / Math.PI;
  d = ((d % 180) + 180) % 180;
  return d >= 180 - 1e-9 ? 0 : d;
}

/** The current value of a valued constraint between two pieces (what a new one
 *  is made with): a distance in mm (unsigned), or an angle in degrees. */
export function measure(type: ObjConstraintType, a: PieceGeom, b: PieceGeom): number {
  switch (type) {
    case 'distance': return len(sub(a.pts[0], b.pts[0]));
    case 'pointLineDistance': return Math.abs(signedLineDistance(a.pts[0], b.pts[0], b.pts[1]));
    case 'lineDistance': return Math.abs(signedLineDistance(b.pts[0], a.pts[0], a.pts[1]));
    case 'angle': return lineAngle(a.pts[0], a.pts[1], b.pts[0], b.pts[1]);
    default: return 0;
  }
}

/** How far a constraint is from true, in mm (angles as the sine of the error
 *  times a metre, so a 0.05° miss is ~1 mm). 0 = satisfied. */
export function constraintError(c: ObjectConstraint, a: PieceGeom | null, b: PieceGeom | null, m?: PieceGeom | null): number {
  if (c.type === 'fix') return 0;
  if (!a || !b) return Infinity;
  const rad = (g: PieceGeom): number => len(sub(g.pts[1], g.pts[0]));
  const [p, q] = [a.pts, b.pts];
  const sinErr = (u: Vec2, v: Vec2, target: number): number => {
    const t = target * Math.PI / 180;
    return Math.abs((crossV(u, v) * Math.cos(t) - (u.x * v.x + u.y * v.y) * Math.sin(t)) / ((len(u) || 1) * (len(v) || 1))) * 1000;
  };
  switch (c.type) {
    case 'coincident': return len(sub(p[0], q[0]));
    case 'horizontal': return Math.abs(p[0].y - q[0].y);
    case 'vertical': return Math.abs(p[0].x - q[0].x);
    case 'distance': return Math.abs(len(sub(p[0], q[0])) - (c.value ?? 0));
    case 'onLine': return Math.abs(signedLineDistance(p[0], q[0], q[1]));
    case 'midpoint': return len(sub(p[0], { x: (q[0].x + q[1].x) / 2, y: (q[0].y + q[1].y) / 2 }));
    case 'pointLineDistance': return Math.abs(Math.abs(signedLineDistance(p[0], q[0], q[1])) - (c.value ?? 0));
    case 'parallel': return sinErr(sub(p[1], p[0]), sub(q[1], q[0]), 0);
    case 'perpendicular': return sinErr(sub(p[1], p[0]), sub(q[1], q[0]), 90);
    case 'collinear': return Math.max(Math.abs(signedLineDistance(q[0], p[0], p[1])), Math.abs(signedLineDistance(q[1], p[0], p[1])));
    case 'equal': return Math.abs(len(sub(p[1], p[0])) - len(sub(q[1], q[0])));
    case 'lineDistance': {
      const v = c.value ?? 0;
      return Math.max(Math.abs(Math.abs(signedLineDistance(q[0], p[0], p[1])) - v), Math.abs(Math.abs(signedLineDistance(q[1], p[0], p[1])) - v));
    }
    case 'angle': return sinErr(sub(p[1], p[0]), sub(q[1], q[0]), c.value ?? 0);
    case 'symmetric': return m ? len(sub(reflectPoint(p[0], m.pts[0], m.pts[1]), q[0])) : Infinity;
    case 'concentric': return len(sub(p[0], q[0]));
    case 'equalRadius': return Math.abs(rad(a) - rad(b));
    case 'tangent': {
      if (b.pts.length === 2 && c.b?.el === 'edge') return Math.abs(Math.abs(signedLineDistance(p[0], q[0], q[1])) - rad(a));
      const d = len(sub(p[0], q[0])), r1 = rad(a), r2 = rad(b);
      return Math.min(Math.abs(d - (r1 + r2)), Math.abs(d - Math.abs(r1 - r2)));
    }
    default: return 0;
  }
}

/** A constraint further from true than this is shown as broken. */
export const BROKEN_TOL = 0.5;

/** The bodies a constraint touches. */
function bodiesOf(c: ObjectConstraint, bodyOf: (id: string) => string): string[] {
  return refsOf(c).map((r) => bodyOf(r.obj));
}

/** The constraints connected (through shared bodies) to any of `seeds`. */
export function networkOf(constraints: ObjectConstraint[], bodyOf: (id: string) => string, seeds: Iterable<string>): ObjectConstraint[] {
  const reached = new Set(seeds);
  const out = new Set<ObjectConstraint>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of constraints) {
      if (out.has(c)) continue;
      const bs = bodiesOf(c, bodyOf);
      if (bs.some((b) => reached.has(b))) {
        out.add(c);
        for (const b of bs) if (!reached.has(b)) { reached.add(b); grew = true; }
      }
    }
  }
  return constraints.filter((c) => out.has(c));
}

/** The solver constraints for one object constraint, over point ids `pa` (its
 *  first piece's points) and `pb`. `cur` measures the current geometry, which
 *  fixes the SIDE of a signed distance. */
function compile(c: ObjectConstraint, pa: string[], pb: string[], a: PieceGeom, b: PieceGeom, pm?: string[]): GcsConstraint[] {
  const id = c.id;
  const rad = (g: PieceGeom): number => len(sub(g.pts[1], g.pts[0]));
  const e = (ids: string[]): GcsEdge => [ids[0], ids[1]];
  const v = c.value ?? 0;
  switch (c.type) {
    case 'coincident': return [{ id, type: 'coincident', points: [pa[0], pb[0]] }];
    case 'horizontal': return [{ id, type: 'vDistance', edges: [[pa[0], pb[0]]], value: 0 }];
    case 'vertical': return [{ id, type: 'hDistance', edges: [[pa[0], pb[0]]], value: 0 }];
    case 'distance': return [{ id, type: 'distance', edges: [[pa[0], pb[0]]], value: v }];
    case 'onLine': return [{ id, type: 'pointOnLine', points: [pa[0]], edges: [e(pb)] }];
    case 'midpoint': return [{ id, type: 'midpoint', points: [pa[0], pb[0], pb[1]] }];
    case 'pointLineDistance': {
      const side = signedLineDistance(a.pts[0], b.pts[0], b.pts[1]) < 0 ? -1 : 1;
      return [{ id, type: 'pointLineDistance', points: [pa[0]], edges: [e(pb)], value: side * v }];
    }
    case 'parallel': return [{ id, type: 'parallel', edges: [e(pa), e(pb)] }];
    case 'perpendicular': return [{ id, type: 'perpendicular', edges: [e(pa), e(pb)] }];
    case 'collinear': return [{ id, type: 'collinear', edges: [e(pa), e(pb)] }];
    case 'equal': return [{ id, type: 'equal', edges: [e(pa), e(pb)] }];
    case 'lineDistance': {
      const side = signedLineDistance(b.pts[0], a.pts[0], a.pts[1]) < 0 ? -1 : 1;
      return [
        { id: id + ':0', type: 'pointLineDistance', points: [pb[0]], edges: [e(pa)], value: side * v },
        { id: id + ':1', type: 'pointLineDistance', points: [pb[1]], edges: [e(pa)], value: side * v },
      ];
    }
    case 'angle': return [{ id, type: 'edgeAngle', edges: [e(pa), e(pb)], value: v }];
    case 'symmetric': return pm ? [{ id, type: 'symmetric', points: [pa[0], pb[0]], edges: [e(pm)] }] : [];
    case 'concentric': return [{ id, type: 'coincident', points: [pa[0], pb[0]] }];
    // Equal lengths of the two centre→rim radii (a body that can't scale makes it
    // a matter of the other one scaling, stage 2).
    case 'equalRadius': return [{ id, type: 'equal', edges: [e(pa), e(pb)] }];
    case 'tangent': {
      if (c.b?.el === 'edge') { // a line: its distance from the centre is the radius, on the side it is
        const side = signedLineDistance(a.pts[0], b.pts[0], b.pts[1]) < 0 ? -1 : 1;
        return [{ id, type: 'pointLineDistance', points: [pa[0]], edges: [e(pb)], value: side * rad(a) }];
      }
      const r1 = rad(a), r2 = rad(b);
      const d = internalTangent(a.pts[0], r1, b.pts[0], r2) ? Math.abs(r1 - r2) : r1 + r2;
      return [{ id, type: 'distance', edges: [[pa[0], pb[0]]], value: d }];
    }
    default: return [];
  }
}

/** Solve the network of `input.constraints` (all of them are solved together;
 *  pass `networkOf(...)` to restrict it). Never mutates the input. */
interface NetworkSketch {
  points: GcsPoint[];
  sk: GcsConstraint[];
  angleEdges: GcsEdge[];
  rigidPts: Map<string, Array<{ id: string; p: Vec2 }>>;
  deforming: Set<string>;
  ids: Map<ObjectConstraint, [string[], string[], string[]?]>;
  /** Pulls holding a deforming body's pinned vertices where they are. */
  pins: Array<{ point: string; x: number; y: number; weight: number }>;
}

/** The network as one gcs sketch: each free rigid body a frame + a `rigid` rule,
 *  each deforming body its vertices + own locks, held bodies' points fixed. */
function buildNetworkSketch(input: NetworkInput, deform: boolean): NetworkSketch | null {
  const cons = input.constraints.filter((c) => c.type !== 'fix');
  {
    const points: GcsPoint[] = [];
    const sk: GcsConstraint[] = [];
    const angleEdges: GcsEdge[] = [];
    // Per free rigid body: its points beyond the frame, with their world position.
    const rigidPts = new Map<string, Array<{ id: string; p: Vec2 }>>();
    const deforming = new Set<string>();
    const usedBodies = new Set<string>();
    for (const c of cons) for (const b of bodiesOf(c, input.bodyOf)) usedBodies.add(b);
    for (const bid of usedBodies) {
      const body = input.bodies.get(bid);
      if (!body) return null;
      if (!body.held && deform && body.deform && body.deform.verts.length >= 2) {
        deforming.add(bid);
        const d = body.deform;
        d.verts.forEach((p, k) => points.push({ id: `${bid}|v${k}`, x: p.x, y: p.y }));
        const n = d.verts.length, segs = d.closed ? n : n - 1;
        for (let k = 0; k < segs; k++) angleEdges.push([`${bid}|v${k}`, `${bid}|v${(k + 1) % n}`]);
        // The sketch's own FIXED points (references its locks are measured from, e.g.
        // a turned room's own axes) come along; its ring points are the vertices.
        for (const op of d.own?.points ?? []) if (op.fixed && !/^v\d+$/.test(op.id)) points.push({ ...op, id: `${bid}|${op.id}` });
        for (const oc of d.own?.constraints ?? []) {
          const map = (pid: string): string => `${bid}|${pid}`;
          sk.push({ ...oc, id: `${bid}|${oc.id}`, points: oc.points?.map(map), edges: oc.edges?.map(([x, y]) => [map(x), map(y)] as GcsEdge) });
        }
      } else if (!body.held) {
        points.push({ id: `${bid}|f0`, x: body.frame[0].x, y: body.frame[0].y });
        points.push({ id: `${bid}|f1`, x: body.frame[1].x, y: body.frame[1].y });
        rigidPts.set(bid, []);
      }
    }
    // Each piece's points: a free rigid body's join its rigid set; a deforming
    // body's are tied to their control vertex by a fixed offset; a held body's are
    // fixed where they are.
    const addPiece = (c: ObjectConstraint, which: 'a' | 'b' | 'm', ref: ConstraintRef, g: PieceGeom): string[] | null => {
      const bid = input.bodyOf(ref.obj);
      const body = input.bodies.get(bid)!;
      return g.pts.map((p, k) => {
        const id = `${c.id}|${which}${k}`;
        if (body.held) { points.push({ id, x: p.x, y: p.y, fixed: true }); return id; }
        points.push({ id, x: p.x, y: p.y });
        if (deforming.has(bid)) {
          const ct = g.ctrl?.[k];
          if (!ct) return null as unknown as string;
          const v = `${bid}|v${ct.i}`;
          sk.push({ id: `${id}|ox`, type: 'hDistance', edges: [[v, id]], value: ct.off.x });
          sk.push({ id: `${id}|oy`, type: 'vDistance', edges: [[v, id]], value: ct.off.y });
        } else rigidPts.get(bid)!.push({ id, p });
        return id;
      });
    };
    const ids = new Map<ObjectConstraint, [string[], string[], string[]?]>();
    const bad = (x: string[] | null): boolean => !x || x.includes(null as unknown as string);
    for (const c of cons) {
      const a = input.piece(c.a), b = c.b ? input.piece(c.b) : null, m = c.m ? input.piece(c.m) : null;
      if (!a || !b || (c.m && !m)) return null;
      const pa = addPiece(c, 'a', c.a, a), pb = addPiece(c, 'b', c.b!, b), pm = m ? addPiece(c, 'm', c.m!, m) : undefined;
      if (bad(pa) || bad(pb) || (pm !== undefined && bad(pm))) return null;
      ids.set(c, [pa!, pb!, pm ?? undefined]);
      sk.push(...compile(c, pa!, pb!, a, b, pm ?? undefined));
    }
    for (const [bid, pts] of rigidPts) {
      const body = input.bodies.get(bid)!;
      const [f0, f1] = body.frame;
      const d = sub(f1, f0), L = len(d);
      if (L < 1e-6) return null;
      const ux = d.x / L, uy = d.y / L;
      sk.push({
        id: `${bid}|rigid`, type: 'rigid', points: [`${bid}|f0`, `${bid}|f1`, ...pts.map((q) => q.id)], value: L,
        local: pts.map((q) => { const r = sub(q.p, f0); return [r.x * ux + r.y * uy, -r.x * uy + r.y * ux]; }),
      });
    }
    // Two passes. A body that must travel far is badly nonlinear for one LM step:
    // its frame's second point lags behind and the frame's own length then drags
    // it, so LM crawls. The first pass prefers every frame (and, deforming, every
    // edge) NOT to turn — that moves bodies as wholes and lands near the answer,
    // only a little short on any turn a constraint asks for; the second pass,
    // started there with no preference, makes the hard constraints exact.
    for (const bid of rigidPts.keys()) angleEdges.push([`${bid}|f0`, `${bid}|f1`]);
    const pins = [...deforming].flatMap((bid) => {
      const d = input.bodies.get(bid)!.deform!;
      return (d.pin ?? []).map((k) => ({ point: `${bid}|v${k}`, x: d.verts[k].x, y: d.verts[k].y, weight: 0.5 }));
    });
    return { points, sk, angleEdges, rigidPts, deforming, ids, pins };
  }
}

/**
 * Solve a network. `pull` names free bodies an edit just put somewhere (a drag):
 * instead of holding them they are pulled toward where they are, free to turn, so
 * a body its constraints won't let stay there moves as close as they allow —
 * swinging about a corner tied to a fixed object rather than sticking.
 */
export function solveNetwork(input: NetworkInput, opts: { live?: boolean; pull?: Iterable<string> } = {}): NetworkResult {
  const pulled = new Set(opts.pull ?? []);
  const fail: NetworkResult = { ok: false, stage: 0, moves: new Map(), deformed: new Map() };
  const stage = (deform: boolean): NetworkResult | null => {
    const built = buildNetworkSketch(input, deform);
    if (!built) return null;
    const { points, sk, angleEdges, rigidPts, deforming, ids, pins } = built;
    const base = { diagnostics: false as const, ...(opts.live ? { restarts: 1, maxIter: 40 } : {}) };
    // The no-turn preference is in radians against residuals in mm: weight it so a
    // degree of turn costs like ~17 mm of error, i.e. pass 1 all but freezes the
    // turn and only carries bodies across; pass 2 makes any turn the constraints need.
    // A pulled body's frame is drawn toward where the edit put it (and may turn);
    // pass 2 then makes the constraints exact from that pose.
    const pulls = [...pulled].filter((bid) => rigidPts.has(bid)).flatMap((bid) => {
      const f = input.bodies.get(bid)!.frame;
      return [{ point: `${bid}|f0`, x: f[0].x, y: f[0].y, weight: 0.3 }, { point: `${bid}|f1`, x: f[1].x, y: f[1].y, weight: 0.3 }];
    });
    const frozen = angleEdges.filter(([a]) => !pulled.has(a.split('|')[0]));
    const near = solveSketch({ points, constraints: sk }, { ...base, angleEdges: frozen, angleWeight: 1000, pulls: [...pulls, ...pins] });
    if (!near.ok) return null;
    const warm = new Map(near.points.map((p) => [p.id, p]));
    // Pass 2's position anchor is all but off: an anchor pulling against a turn
    // leaves an angle a fraction of a degree short, which the solver's own conflict
    // test (a mean residual) would still accept.
    // Every constraint must hold to the drawing's own tolerance, not the solver's.
    const holds = (at: Map<string, Vec2>): boolean => [...ids].every(([c, [pa, pb, pm]]) =>
      constraintError(c, { pts: pa.map((id) => at.get(id)!) }, { pts: pb.map((id) => at.get(id)!) }, pm ? { pts: pm.map((id) => at.get(id)!) } : null) <= BROKEN_TOL);
    // A body's start turned by `turn` about its frame's first point.
    const start = (turn: number): GcsPoint[] => {
      const owner = new Map<string, string>();
      for (const [bid, pts] of rigidPts) for (const id of [`${bid}|f0`, `${bid}|f1`, ...pts.map((q) => q.id)]) owner.set(id, bid);
      const c = Math.cos(turn), sn = Math.sin(turn);
      return points.map((p) => {
        if (p.fixed) return p;
        const w = warm.get(p.id)!;
        const bid = owner.get(p.id);
        if (!turn || !bid) return { ...p, x: w.x, y: w.y };
        const o = warm.get(`${bid}|f0`)!;
        return { ...p, x: o.x + (w.x - o.x) * c - (w.y - o.y) * sn, y: o.y + (w.x - o.x) * sn + (w.y - o.y) * c };
      });
    };
    // An angle asked of two edges that are square to what it wants (a perpendicular
    // of two parallel edges, a parallel of two square ones) starts on a saddle: the
    // residual has no slope there and the solver cannot begin the turn. So a failed
    // start is retried from each body turned a little either way.
    const angular = [...ids.keys()].some((c) => c.type === 'perpendicular' || c.type === 'parallel' || c.type === 'angle' || c.type === 'collinear');
    let at: Map<string, Vec2> | null = null;
    for (const turn of angular ? [0, 0.35, -0.35] : [0]) {
      const r = solveSketch({ points: start(turn), constraints: sk }, { ...base, regWeight: 1e-7 });
      if (!r.ok) continue;
      const got = new Map(r.points.map((p) => [p.id, { x: p.x, y: p.y }]));
      if (holds(got)) { at = got; break; }
    }
    if (!at) return null;
    const moves = new Map<string, BodyMove>();
    for (const bid of rigidPts.keys()) {
      const body = input.bodies.get(bid)!;
      const [f0, f1] = body.frame;
      const g0 = at.get(`${bid}|f0`)!, g1 = at.get(`${bid}|f1`)!;
      const rot = Math.atan2(g1.y - g0.y, g1.x - g0.x) - Math.atan2(f1.y - f0.y, f1.x - f0.x);
      moves.set(bid, { pivot: f0, dx: g0.x - f0.x, dy: g0.y - f0.y, rot });
    }
    const deformed = new Map<string, Vec2[]>();
    for (const bid of deforming) {
      const d = input.bodies.get(bid)!.deform!;
      const verts = d.verts.map((_, k) => at.get(`${bid}|v${k}`)!);
      if (collapsed(d.verts, verts, d.closed)) return null;
      deformed.set(d.obj, verts);
    }
    return { ok: true, stage: deform ? 2 : 1, moves, deformed };
  };
  return stage(false) ?? stage(true) ?? fail;
}

/** A deformation that shrinks a real edge below 1 mm satisfies its constraint
 *  only by collapsing the shape — refuse it. */
function collapsed(before: Vec2[], after: Vec2[], closed: boolean): boolean {
  const n = after.length, segs = closed ? n : n - 1;
  for (let k = 0; k < segs; k++) {
    const j = (k + 1) % n;
    if (len(sub(before[j], before[k])) >= 1 && len(sub(after[j], after[k])) < 1) return true;
  }
  return false;
}

/** Apply a rigid move to a world point. */
export function applyMove(m: BodyMove, p: Vec2): Vec2 {
  const c = Math.cos(m.rot), s = Math.sin(m.rot);
  const rx = p.x - m.pivot.x, ry = p.y - m.pivot.y;
  return { x: m.pivot.x + rx * c - ry * s + m.dx, y: m.pivot.y + rx * s + ry * c + m.dy };
}

/** Is a move worth applying (more than a hundredth of a mm / a millidegree)? */
export function isNoMove(m: BodyMove): boolean {
  return Math.abs(m.dx) < 1e-2 && Math.abs(m.dy) < 1e-2 && Math.abs(m.rot) < 2e-5;
}

/** Two distinct points of `pts` as far apart as possible — a body's frame. */
export function frameOf(pts: Vec2[]): [Vec2, Vec2] | null {
  if (pts.length < 2) return null;
  let best: [Vec2, Vec2] | null = null, bd = 1e-6;
  for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
    const d = len(sub(pts[i], pts[j]));
    if (d > bd) { bd = d; best = [pts[i], pts[j]]; }
  }
  return best;
}

// ---- Badges ----
// Each constraint shows a small badge beside every piece it holds (screen px).
// The layout is the ONE list both the renderer draws and the click tests, so a
// badge cannot be drawn where nothing answers.

/** A badge to place: beside `anchor`, pushed off it along the unit `dir`. */
export interface BadgeSpec { cid: string; anchor: Vec2; dir: Vec2; text: string }
/** A placed badge: centre `x, y`, size `w × h` (screen px). */
export interface BadgeBox { cid: string; x: number; y: number; w: number; h: number; text: string; anchor: Vec2 }

export const BADGE_H = 16;
const BADGE_GAP = 14;

export function badgeWidth(text: string): number { return 8 + 7 * [...text].length; }

/** Place the badges in order, each pushed further along its direction until it
 *  overlaps none placed before it. */
export function layoutBadges(specs: BadgeSpec[]): BadgeBox[] {
  const out: BadgeBox[] = [];
  const hits = (b: BadgeBox): boolean => out.some((o) => Math.abs(o.x - b.x) * 2 < o.w + b.w + 2 && Math.abs(o.y - b.y) * 2 < o.h + b.h + 2);
  for (const s of specs) {
    const w = badgeWidth(s.text), h = BADGE_H;
    const d = len(s.dir) > 1e-9 ? { x: s.dir.x / len(s.dir), y: s.dir.y / len(s.dir) } : { x: 0.7071, y: -0.7071 };
    const reach = BADGE_GAP + Math.abs(d.x) * w / 2 + Math.abs(d.y) * h / 2;
    const step = Math.abs(d.x) * (w + 2) + Math.abs(d.y) * (h + 2);
    const b: BadgeBox = { cid: s.cid, x: s.anchor.x + d.x * reach, y: s.anchor.y + d.y * reach, w, h, text: s.text, anchor: s.anchor };
    for (let k = 0; k < 12 && hits(b); k++) { b.x += d.x * step; b.y += d.y * step; }
    out.push(b);
  }
  return out;
}

/** The constraint whose badge is under screen point `p` (the last drawn wins). */
export function badgeAt(boxes: BadgeBox[], p: Vec2, pad = 2): string | null {
  for (let i = boxes.length - 1; i >= 0; i--) {
    const b = boxes[i];
    if (Math.abs(p.x - b.x) <= b.w / 2 + pad && Math.abs(p.y - b.y) <= b.h / 2 + pad) return b.cid;
  }
  return null;
}

// ---- Renumbering ----
// A constraint names a piece by index, so a vertex inserted into or deleted from
// its object renumbers it. Indices run over the object's rings in order (a
// polygon's holes after its outline; a room's corners and walls by WALL).

type Ref = ConstraintRef;
const mapRefs = (cs: ObjectConstraint[], obj: string, f: (r: Ref) => Ref | null): ObjectConstraint[] => {
  const out: ObjectConstraint[] = [];
  for (const c of cs) {
    // A curve (a circle, an arc) has no numbering a vertex edit could shift.
    const g = (r: Ref): Ref | null => (r.el === 'curve' ? r : f(r));
    const a = c.a.obj === obj ? g(c.a) : c.a;
    const b = c.b && c.b.obj === obj ? g(c.b) : c.b;
    const m = c.m && c.m.obj === obj ? g(c.m) : c.m;
    if (!a || (c.b && !b) || (c.m && !m)) continue;
    out.push(a === c.a && b === c.b && m === c.m ? c : { ...c, a, ...(b ? { b } : {}), ...(m ? { m } : {}) });
  }
  return out;
};

/** A vertex was inserted after vertex `after` (so it is `after + 1`): later
 *  vertices and edges move up one; the split edge's constraints stay on its first
 *  half. */
export function renumberOnInsert(cs: ObjectConstraint[], obj: string, after: number): ObjectConstraint[] {
  return mapRefs(cs, obj, (r) => (r.el !== 'object' && r.index > after ? { ...r, index: r.index + 1 } : r));
}

/** Vertex `v` was deleted from the ring spanning indices `[base, base + len)`
 *  (`closed` for an outline, open for a wall or line). Constraints on the vertex go;
 *  the two edges it joined become one; an open path's end edge goes with its end. */
export function renumberOnRemove(cs: ObjectConstraint[], obj: string, v: number, ring: { base: number; len: number; closed: boolean }): ObjectConstraint[] {
  const { base, len, closed } = ring;
  const i = v - base;
  return mapRefs(cs, obj, (r) => {
    if (r.el === 'object') return r;
    if (r.el === 'vertex') return r.index === v ? null : r.index > v ? { ...r, index: r.index - 1 } : r;
    const e = r.index;
    if (e < base) return r;
    const k = e - base;
    if (closed) {
      if (k >= len) return { ...r, index: e - 1 };          // a later ring
      if (i === 0) return { ...r, index: k === 0 ? base + len - 2 : e - 1 };
      return k >= i ? { ...r, index: e - 1 } : r;
    }
    if (i === 0) return k === 0 ? null : { ...r, index: e - 1 };
    if (i === len - 1) return k === len - 2 ? null : r;
    return k >= i ? { ...r, index: e - 1 } : r;
  });
}

/** A whole ring (a hole) spanning `[base, base + len)` went: its constraints go,
 *  later pieces move down. */
export function renumberOnRingRemove(cs: ObjectConstraint[], obj: string, base: number, len: number): ObjectConstraint[] {
  return mapRefs(cs, obj, (r) => (r.el === 'object' || r.index < base ? r : r.index < base + len ? null : { ...r, index: r.index - len }));
}

// ---- Finding a piece again by where it was ----
// An index is only a name for a piece while the object keeps its shape's
// numbering. Any edit that changes how many pieces an object has (a vertex in or
// out, an explode, a join, a trim, a fillet) is caught by `matchPiece`: the piece
// is found again where it was at the last solve. One rule for every editing path,
// so none of them needs to know about constraints.

export interface PieceCandidate { ref: ConstraintRef; pts: Vec2[] }

/**
 * The candidate that is the piece last seen at `at` (one point = a vertex, two =
 * an edge), or null when it is gone. A vertex must still be where it was; an edge
 * may have been split or trimmed, so it is the candidate lying ON the old edge's
 * line, parallel, that overlaps the old run the most (an unchanged edge overlaps
 * it fully and wins).
 */
export function matchPiece(at: Vec2[], candidates: PieceCandidate[], tol = 0.5): PieceCandidate | null {
  if (at.length === 1) {
    let best: PieceCandidate | null = null, bd = tol;
    for (const c of candidates) {
      if (c.pts.length !== 1) continue;
      const d = len(sub(c.pts[0], at[0]));
      if (d <= bd) { bd = d; best = c; }
    }
    return best;
  }
  const [a, b] = at;
  const L = len(sub(b, a));
  if (L < 1e-9) return null;
  const u = { x: (b.x - a.x) / L, y: (b.y - a.y) / L };
  const off = (p: Vec2): number => Math.abs(crossV(u, sub(p, a)));
  const along = (p: Vec2): number => u.x * (p.x - a.x) + u.y * (p.y - a.y);
  let best: PieceCandidate | null = null, bo = tol;
  for (const c of candidates) {
    if (c.pts.length !== 2 || off(c.pts[0]) > tol || off(c.pts[1]) > tol) continue;
    const s = along(c.pts[0]), t = along(c.pts[1]);
    const overlap = Math.min(L, Math.max(s, t)) - Math.max(0, Math.min(s, t));
    if (overlap > bo) { bo = overlap; best = c; }
  }
  return best;
}

// ---- How defined each body is ----

/**
 * Which bodies the network still leaves free to move (`'free'`) and which it pins
 * (`'defined'`) — SolidWorks' under- / fully-defined. Held bodies are the anchors
 * (the fixed objects); with none, a network is judged up to moving it all as one
 * (it can't be pinned to the drawing, only its pieces to each other). A body is
 * free when its frame can still move on its own. `conflicting` when the drawing
 * as it stands breaks the network.
 */
export function networkStatus(input: NetworkInput): { bodies: Map<string, 'free' | 'defined'>; conflicting: boolean } | null {
  const built = buildNetworkSketch(input, false);
  if (!built) return null;
  const r = solveSketch({ points: built.points, constraints: built.sk }, { maxIter: 1, restarts: 0 });
  if (!r.ok) return null;
  const free = new Set(r.points.filter((p) => p.free).map((p) => p.id));
  const bodies = new Map<string, 'free' | 'defined'>();
  for (const [bid, body] of input.bodies) {
    if (body.held) { bodies.set(bid, 'defined'); continue; }
    if (!built.rigidPts.has(bid)) continue;
    bodies.set(bid, free.has(`${bid}|f0`) || free.has(`${bid}|f1`) ? 'free' : 'defined');
  }
  let conflicting = false;
  for (const [c, [pa, pb, pm]] of built.ids) {
    const at = (id: string): Vec2 => built.points.find((p) => p.id === id)!;
    if (constraintError(c, { pts: pa.map(at) }, { pts: pb.map(at) }, pm ? { pts: pm.map(at) } : null) > BROKEN_TOL) conflicting = true;
  }
  return { bodies, conflicting };
}

// ---- A shape's own axes ----

/**
 * A shape's own sketch, built over its WORLD outline, whose absolute locks are
 * meant in the shape's OWN frame (a turned room's): `toWorld` maps a point of that
 * frame to the world. A horizontal / vertical edge becomes an angle of 0° / 90°
 * to the frame's x axis (a fixed reference edge), a Lock X / Lock Y a point on the
 * frame's own vertical / horizontal line. Everything else is frame-free already.
 */
export function ownAxesSketch(sk: GcsSketch, toWorld: (p: Vec2) => Vec2): GcsSketch {
  const points: GcsPoint[] = [...sk.points, { id: 'ax0', ...toWorld({ x: 0, y: 0 }), fixed: true }, { id: 'ax1', ...toWorld({ x: 1000, y: 0 }), fixed: true }];
  const constraints: GcsConstraint[] = [];
  for (const c of sk.constraints) {
    if (c.type === 'horizontal' || c.type === 'vertical') {
      constraints.push({ id: c.id, type: 'edgeAngle', edges: [['ax0', 'ax1'], c.edges![0]], value: c.type === 'horizontal' ? 0 : 90 });
    } else if (c.type === 'lockX' || c.type === 'lockY') {
      const v = c.value ?? 0;
      const [p, q] = c.type === 'lockX' ? [toWorld({ x: v, y: 0 }), toWorld({ x: v, y: 1000 })] : [toWorld({ x: 0, y: v }), toWorld({ x: 1000, y: v })];
      points.push({ id: `${c.id}p`, ...p, fixed: true }, { id: `${c.id}q`, ...q, fixed: true });
      constraints.push({ id: c.id, type: 'pointOnLine', points: c.points, edges: [[`${c.id}p`, `${c.id}q`]] });
    } else constraints.push(c);
  }
  return { points, constraints };
}
